import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons, CurrencyInput } from '../../shared/ui';
const { DollarSign: CurrencyDollarIcon, Zap: BoltIcon, Droplets: BeakerIcon, Wifi: WifiIcon, Truck: TruckIcon, Sparkles: SparklesIcon, Plus: PlusIcon, Trash: TrashIcon, ChevronDown: ChevronDownIcon, ChevronUp: ChevronUpIcon, Check: CheckIcon, X: XMarkIcon, Edit: PencilIcon, Cog: Cog6ToothIcon } = Icons;

// Default services configuration — names are seed data stored verbatim in the DB;
// display names are translated via settings:service.name.<service_id> in the component.
export const defaultServicesConfig = [
  { service_id: 'electric', name: 'Tiền điện', category: 'fundamental', type: 'metered', unit_price: 4000, unit: 'kWh', is_active: true, is_included: false },
  { service_id: 'water', name: 'Tiền nước', category: 'fundamental', type: 'metered', unit_price: 25000, unit: 'm3', is_active: true, is_included: false },
  { service_id: 'internet', name: 'Internet', category: 'optional', type: 'fixed_per_person', unit_price: 50000, unit: 'person', is_active: true, is_included: false },
  { service_id: 'washing', name: 'Giặt đồ', category: 'optional', type: 'fixed_per_person', unit_price: 75000, unit: 'person', is_active: true, is_included: false },
  { service_id: 'elevator', name: 'Thang máy', category: 'optional', type: 'fixed_per_person', unit_price: 50000, unit: 'person', is_active: true, is_included: false },
  { service_id: 'cleaning', name: 'Vệ sinh', category: 'optional', type: 'fixed_per_person', unit_price: 30000, unit: 'person', is_active: true, is_included: false },
  { service_id: 'parking_motorbike', name: 'Gửi xe máy', category: 'optional', type: 'fixed_per_room', unit_price: 100000, unit: 'vehicle', is_active: true, is_included: false },
  { service_id: 'parking_car', name: 'Gửi ô tô', category: 'optional', type: 'fixed_per_room', unit_price: 500000, unit: 'vehicle', is_active: true, is_included: false }
];

// Icon mapping for services
const getServiceIcon = (serviceId) => {
  const icons = {
    electric: BoltIcon,
    water: BeakerIcon,
    internet: WifiIcon,
    parking_motorbike: TruckIcon,
    parking_car: TruckIcon,
    washing: SparklesIcon,
    elevator: Cog6ToothIcon,
    cleaning: SparklesIcon
  };
  return icons[serviceId] || CurrencyDollarIcon;
};

const formatCurrency = (amount) => new Intl.NumberFormat('vi-VN').format(amount);

/**
 * ServiceConfigManager - Component for managing services configuration
 *
 * @param {Array} services - Current services_config array
 * @param {Function} onChange - Callback when services change
 * @param {boolean} readOnly - If true, only display mode (no editing)
 * @param {boolean} compact - Compact display mode
 * @param {string} title - Custom title for the section
 */
const ServiceConfigManager = ({
  services = [],
  onChange,
  readOnly = false,
  compact = false,
  title
}) => {
  const { t } = useTranslation('settings');
  const [expandedServices, setExpandedServices] = useState({});
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({
    service_id: '',
    name: '',
    category: 'optional',
    type: 'fixed_per_room',
    unit_price: 0,
    unit: 'month',
    is_active: true,
    is_included: false
  });

  const displayTitle = title ?? t('service.config.title', 'Service Configuration');

  // Category and type option lists built from t() so they re-render on language change
  const categories = [
    { value: 'fundamental', label: t('service.category.fundamental') },
    { value: 'optional',    label: t('service.category.optional') }
  ];
  const serviceTypes = [
    { value: 'metered',          label: t('service.type.metered'),         description: t('service.config.description') },
    { value: 'fixed_per_person', label: t('service.type.fixed_per_person') },
    { value: 'fixed_per_room',   label: t('service.type.fixed_per_room') },
    { value: 'one_time',         label: t('service.type.one_time') }
  ];

  const servicesList = Array.isArray(services) ? services : [];

  // Translate display name for built-in services; fall back to stored name for custom services
  const getDisplayName = (service) =>
    t(`service.name.${service.service_id}`, service.name);

  const getCategoryLabel = (categoryValue) =>
    categories.find(c => c.value === categoryValue)?.label ?? categoryValue;

  const getTypeLabel = (typeValue) =>
    serviceTypes.find(t => t.value === typeValue)?.label ?? typeValue;

  const getCategoryColor = (category) =>
    category === 'fundamental'
      ? 'bg-teal-100 text-teal-700 border-teal-200'
      : 'bg-purple-100 text-purple-700 border-purple-200';

  const toggleExpand = (serviceId) =>
    setExpandedServices(prev => ({ ...prev, [serviceId]: !prev[serviceId] }));

  const updateService = (serviceId, field, value) => {
    if (readOnly) return;
    onChange(servicesList.map(s => s.service_id === serviceId ? { ...s, [field]: value } : s));
  };

  const toggleActive = (serviceId) => {
    if (readOnly) return;
    const service = servicesList.find(s => s.service_id === serviceId);
    if (service) updateService(serviceId, 'is_active', !service.is_active);
  };

  const toggleIncluded = (serviceId) => {
    if (readOnly) return;
    const service = servicesList.find(s => s.service_id === serviceId);
    if (service) updateService(serviceId, 'is_included', !service.is_included);
  };

  const handleAddService = () => {
    if (!newService.service_id || !newService.name) return;
    if (servicesList.some(s => s.service_id === newService.service_id)) {
      alert(t('service.config.idExists', 'Service ID already exists'));
      return;
    }
    onChange([...servicesList, { ...newService }]);
    setNewService({ service_id: '', name: '', category: 'optional', type: 'fixed_per_room', unit_price: 0, unit: 'month', is_active: true, is_included: false });
    setShowAddService(false);
  };

  const removeService = (serviceId) => {
    if (readOnly) return;
    if (window.confirm(t('service.config.removeConfirm'))) {
      onChange(servicesList.filter(s => s.service_id !== serviceId));
    }
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-stone-700">{displayTitle}</h4>
        <div className="grid grid-cols-2 gap-2">
          {servicesList.filter(s => s.is_active).map(service => (
            <div
              key={service.service_id}
              className={`flex items-center justify-between p-2 rounded-lg border ${
                service.is_included ? 'bg-green-50 border-green-200' : 'bg-white border-stone-200'
              }`}
            >
              <div className="flex items-center gap-2">
                {React.createElement(getServiceIcon(service.service_id), { className: 'h-4 w-4 text-stone-500' })}
                <span className="text-sm font-medium text-stone-700">{getDisplayName(service)}</span>
              </div>
              <div className="text-sm">
                {service.is_included ? (
                  <span className="text-green-600 text-xs font-medium">{t('service.config.included')}</span>
                ) : (
                  <span className="text-stone-900 font-semibold">
                    {formatCurrency(service.unit_price)} <span className="text-xs text-stone-500">/{service.unit}</span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-5 rounded-xl border border-emerald-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Cog6ToothIcon size={20} className="text-emerald-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-stone-800">{displayTitle}</h3>
            <p className="text-xs text-stone-500">{t('service.config.description')}</p>
          </div>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => setShowAddService(!showAddService)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-100 rounded-lg hover:bg-emerald-200 transition-colors"
          >
            <PlusIcon size={16} />
            {t('service.config.addService')}
          </button>
        )}
      </div>

      {/* Add New Service Form */}
      {showAddService && !readOnly && (
        <div className="mb-4 p-4 bg-white rounded-lg border border-emerald-200 space-y-3">
          <h4 className="text-sm font-semibold text-stone-700">{t('service.config.addForm.title')}</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">{t('service.config.addForm.serviceId')}</label>
              <input
                type="text"
                value={newService.service_id}
                onChange={(e) => setNewService({ ...newService, service_id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder={t('service.config.addForm.idPlaceholder')}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">{t('service.config.addForm.serviceName')}</label>
              <input
                type="text"
                value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                placeholder={t('service.config.addForm.namePlaceholder')}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">{t('service.config.addForm.category')}</label>
              <select
                value={newService.category}
                onChange={(e) => setNewService({ ...newService, category: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              >
                {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">{t('service.config.addForm.type')}</label>
              <select
                value={newService.type}
                onChange={(e) => setNewService({ ...newService, type: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              >
                {serviceTypes.map(st => <option key={st.value} value={st.value}>{st.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">{t('service.config.addForm.unitPrice')}</label>
              <CurrencyInput
                value={newService.unit_price}
                onChange={(e) => setNewService({ ...newService, unit_price: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">{t('service.config.addForm.unit')}</label>
              <input
                type="text"
                value={newService.unit}
                onChange={(e) => setNewService({ ...newService, unit: e.target.value })}
                placeholder={t('service.config.addForm.unitPlaceholder')}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowAddService(false)} className="px-3 py-1.5 text-sm text-stone-600 hover:text-stone-800">
              {t('common:action.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              onClick={handleAddService}
              disabled={!newService.service_id || !newService.name}
              className="px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('service.config.addForm.add')}
            </button>
          </div>
        </div>
      )}

      {/* Services List */}
      <div className="space-y-2">
        {servicesList.map((service) => {
          const ServiceIcon = getServiceIcon(service.service_id);
          const isExpanded = expandedServices[service.service_id];

          return (
            <div
              key={service.service_id}
              className={`bg-white rounded-lg border transition-all ${
                service.is_active ? 'border-stone-200 hover:border-emerald-300' : 'border-stone-100 opacity-60'
              }`}
            >
              {/* Service Row */}
              <div className="flex items-center gap-3 p-3">
                {/* Icon & Name */}
                <div
                  className="flex items-center gap-3 flex-1 cursor-pointer"
                  onClick={() => !readOnly && toggleExpand(service.service_id)}
                >
                  <div className={`p-2 rounded-lg ${service.is_active ? 'bg-emerald-100' : 'bg-stone-100'}`}>
                    <ServiceIcon size={16} className={`${service.is_active ? 'text-emerald-600' : 'text-stone-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-stone-800 text-sm">{getDisplayName(service)}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getCategoryColor(service.category)}`}>
                        {getCategoryLabel(service.category)}
                      </span>
                      {service.is_included && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 border border-green-200">
                          {t('service.config.included')}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-stone-500">{getTypeLabel(service.type)}</span>
                  </div>
                </div>

                {/* Price */}
                <div className="text-right mr-2">
                  {readOnly ? (
                    <span className="font-semibold text-stone-900">
                      {formatCurrency(service.unit_price)} <span className="text-xs text-stone-500">/{service.unit}</span>
                    </span>
                  ) : (
                    <CurrencyInput
                      value={service.unit_price}
                      onChange={(e) => updateService(service.service_id, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-28 px-2 py-1 text-right border border-stone-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500"
                    />
                  )}
                </div>

                {/* Actions */}
                {!readOnly && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => toggleActive(service.service_id)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        service.is_active ? 'text-green-600 hover:bg-green-50' : 'text-stone-400 hover:bg-stone-50'
                      }`}
                      title={service.is_active ? t('service.config.deactivate') : t('service.config.activate')}
                    >
                      {service.is_active ? <CheckIcon size={16} /> : <XMarkIcon size={16} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleExpand(service.service_id)}
                      className="p-1.5 text-stone-400 hover:text-stone-600 hover:bg-stone-50 rounded-lg"
                    >
                      {isExpanded ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
                    </button>
                    {!['electric', 'water'].includes(service.service_id) && (
                      <button
                        type="button"
                        onClick={() => removeService(service.service_id)}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title={t('common:action.delete', 'Remove service')}
                      >
                        <TrashIcon size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Expanded Details */}
              {isExpanded && !readOnly && (
                <div className="px-3 pb-3 pt-0 border-t border-stone-100 mt-2">
                  <div className="grid grid-cols-3 gap-3 pt-3">
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">{t('service.config.addForm.category')}</label>
                      <select
                        value={service.category}
                        onChange={(e) => updateService(service.service_id, 'category', e.target.value)}
                        className="w-full px-2 py-1.5 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                      >
                        {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">{t('service.config.addForm.type')}</label>
                      <select
                        value={service.type}
                        onChange={(e) => updateService(service.service_id, 'type', e.target.value)}
                        className="w-full px-2 py-1.5 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                      >
                        {serviceTypes.map(tp => <option key={tp.value} value={tp.value}>{tp.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-600 mb-1">{t('service.config.addForm.unit')}</label>
                      <input
                        type="text"
                        value={service.unit || ''}
                        onChange={(e) => updateService(service.service_id, 'unit', e.target.value)}
                        className="w-full px-2 py-1.5 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                        placeholder={t('service.config.addForm.unitPlaceholder')}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={service.is_included || false}
                        onChange={() => toggleIncluded(service.service_id)}
                        className="w-4 h-4 text-emerald-600 rounded border-stone-300 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-stone-700">{t('service.config.includeInRent')}</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info */}
      <div className="mt-4 text-xs text-emerald-600 bg-emerald-100/50 p-2 rounded-lg flex items-start gap-2">
        <span className="text-lg">💡</span>
        <span>{t('service.config.hint')}</span>
      </div>
    </div>
  );
};

export default ServiceConfigManager;
